/**
 * Visitor Management API service for Aegix One.
 */

import { client } from '../lib/http-client';

export interface PreCheckIn {
  public_id: string;
  student: number;
  student_name: string;
  school: number;
  parent: number;
  expected_arrival_at: string;
  status: 'pending' | 'arrived' | 'cancelled';
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface CarRiderQueueEntry {
  public_id: string;
  student: number;
  student_name: string;
  school: number;
  queued_by: number;
  status: 'waiting' | 'called' | 'picked_up' | 'cancelled';
  queued_at: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface PickupDelegation {
  public_id: string;
  student: number;
  student_name: string;
  delegated_by: number;
  delegate_name: string;
  delegate_phone: string;
  delegate_email: string;
  relationship: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// --- PreCheckIn APIs ---

function isForbiddenError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null) {
    const errObj = error as { response?: { status?: number } };
    return errObj.response?.status === 403;
  }
  return false;
}

export async function getPreCheckIns(): Promise<PaginatedResponse<PreCheckIn>> {
  try {
    const response = await client.ky.get('api/v2/visitor-management/pre-checkins/?page_size=100');
    return (await response.json()) as PaginatedResponse<PreCheckIn>;
  } catch (error: unknown) {
    if (isForbiddenError(error)) {
      return { count: 0, next: null, previous: null, results: [] };
    }
    throw error;
  }
}

export async function createPreCheckIn(data: {
  student: number;
  school: number;
  expected_arrival_at?: string;
  notes?: string;
  latitude?: number;
  longitude?: number;
}): Promise<PreCheckIn> {
  const response = await client.ky.post('api/v2/visitor-management/pre-checkins/', {
    json: data,
  });
  return (await response.json()) as PreCheckIn;
}

export async function updatePreCheckIn(
  public_id: string,
  data: Partial<PreCheckIn>,
): Promise<PreCheckIn> {
  const response = await client.ky.patch(`api/v2/visitor-management/pre-checkins/${public_id}/`, {
    json: data,
  });
  return (await response.json()) as PreCheckIn;
}

// --- CarRiderQueueEntry APIs ---

export async function getCarRiderQueue(): Promise<PaginatedResponse<CarRiderQueueEntry>> {
  try {
    const response = await client.ky.get('api/v2/visitor-management/car-rider-queue/?page_size=100');
    return (await response.json()) as PaginatedResponse<CarRiderQueueEntry>;
  } catch (error: unknown) {
    if (isForbiddenError(error)) {
      return { count: 0, next: null, previous: null, results: [] };
    }
    throw error;
  }
}

export async function joinCarRiderQueue(data: {
  student: number;
  school: number;
  notes?: string;
}): Promise<CarRiderQueueEntry> {
  const response = await client.ky.post('api/v2/visitor-management/car-rider-queue/', {
    json: data,
  });
  return (await response.json()) as CarRiderQueueEntry;
}

export async function updateCarRiderQueue(
  public_id: string,
  data: Partial<CarRiderQueueEntry>,
): Promise<CarRiderQueueEntry> {
  const response = await client.ky.patch(`api/v2/visitor-management/car-rider-queue/${public_id}/`, {
    json: data,
  });
  return (await response.json()) as CarRiderQueueEntry;
}

// --- PickupDelegation APIs ---

export async function getPickupDelegations(): Promise<PaginatedResponse<PickupDelegation>> {
  try {
    const response = await client.ky.get('api/v2/visitor-management/pickup-delegations/?page_size=100');
    return (await response.json()) as PaginatedResponse<PickupDelegation>;
  } catch (error: unknown) {
    if (isForbiddenError(error)) {
      return { count: 0, next: null, previous: null, results: [] };
    }
    throw error;
  }
}

export async function createPickupDelegation(data: {
  student: number;
  delegate_name: string;
  delegate_phone?: string;
  delegate_email?: string;
  relationship: string;
  valid_from: string;
  valid_until: string;
  notes?: string;
}): Promise<PickupDelegation> {
  const response = await client.ky.post('api/v2/visitor-management/pickup-delegations/', {
    json: data,
  });
  return (await response.json()) as PickupDelegation;
}

export async function updatePickupDelegation(
  public_id: string,
  data: Partial<PickupDelegation>,
): Promise<PickupDelegation> {
  const response = await client.ky.patch(`api/v2/visitor-management/pickup-delegations/${public_id}/`, {
    json: data,
  });
  return (await response.json()) as PickupDelegation;
}

export async function deletePickupDelegation(public_id: string): Promise<void> {
  await client.ky.delete(`api/v2/visitor-management/pickup-delegations/${public_id}/`);
}

// --- StudentCheckEvent and TardySlip interfaces & APIs ---

export interface TardySlip {
  public_id: string;
  class_period: string;
  printed: boolean;
  printed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface StudentCheckEvent {
  public_id: string;
  student: number;
  student_name: string;
  school: number;
  event_type: 'late_arrival' | 'early_dismissal';
  reason: string;
  timestamp: string;
  parent_notified: boolean;
  teacher_notified: boolean;
  synced_to_sis: boolean;
  created_by?: number;
  tardy_slips?: TardySlip[];
  created_at?: string;
  updated_at?: string;
}

export async function getStudentCheckEvents(): Promise<PaginatedResponse<StudentCheckEvent>> {
  try {
    const response = await client.ky.get('api/v2/visitor-management/student-check-events/?page_size=100');
    return (await response.json()) as PaginatedResponse<StudentCheckEvent>;
  } catch (error: unknown) {
    if (isForbiddenError(error)) {
      return { count: 0, next: null, previous: null, results: [] };
    }
    throw error;
  }
}

export async function createStudentCheckEvent(data: {
  student: number;
  school: number;
  event_type: 'late_arrival' | 'early_dismissal';
  reason: string;
  timestamp: string;
  parent_notified?: boolean;
  teacher_notified?: boolean;
}): Promise<StudentCheckEvent> {
  const response = await client.ky.post('api/v2/visitor-management/student-check-events/', {
    json: data,
  });
  return (await response.json()) as StudentCheckEvent;
}

export async function updateStudentCheckEvent(
  public_id: string,
  data: Partial<StudentCheckEvent>,
): Promise<StudentCheckEvent> {
  const response = await client.ky.patch(`api/v2/visitor-management/student-check-events/${public_id}/`, {
    json: data,
  });
  return (await response.json()) as StudentCheckEvent;
}

export async function createTardySlip(
  event_public_id: string,
  data: { class_period: string },
): Promise<TardySlip> {
  const response = await client.ky.post(
    `api/v2/visitor-management/student-check-events/${event_public_id}/tardy-slip/`,
    {
      json: data,
    },
  );
  return (await response.json()) as TardySlip;
}

export async function getTardySlipHtml(event_public_id: string): Promise<string> {
  const response = await client.ky.get(
    `api/v2/visitor-management/student-check-events/${event_public_id}/tardy-slip/`,
  );
  return response.text();
}


